import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import InfoIcon from "@/assets/svgs/info.svg";
import { validateCSVHeaders, FORMAT_GUIDELINES } from '../csv-template/template-generator';
import { CreateProductInput } from '@/services/product-catalogue/pc-api';

interface CSVColumnMapperProps {
  csvHeaders: string[];
  csvData: any[];
  onMappingChange: (mapping: ColumnMapping) => void;
  onValidDataChange: (validData: CreateProductInput[]) => void;
}

export interface ColumnMapping {
  [csvHeader: string]: keyof CreateProductInput | 'ignore';
}

const PRODUCT_FIELDS = {
  sku: 'Product SKU *',
  name: 'Product Name *',
  description: 'Description',
  htsCode: 'HTS Code *',
  countryOfOrigin: 'Country of Origin *',
  metalPercentage: 'Metal Percentage (%)',
  ignore: 'Ignore Column'
};

const CSVColumnMapper: React.FC<CSVColumnMapperProps> = ({
  csvHeaders,
  csvData,
  onMappingChange,
  onValidDataChange,
}) => {
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [validationResults, setValidationResults] = useState<any>(null);

  // Auto-suggest mappings when headers change
  useEffect(() => {
    const suggestions = validateCSVHeaders(csvHeaders);
    const autoMapping: ColumnMapping = {};
    
    csvHeaders.forEach(header => {
      const suggestion = suggestions.suggestions[header];
      if (suggestion) {
        autoMapping[header] = suggestion as keyof CreateProductInput;
      } else {
        autoMapping[header] = 'ignore';
      }
    });
    
    setMapping(autoMapping);
    setValidationResults(suggestions);
  }, [csvHeaders]);

  // Update parent when mapping changes
  useEffect(() => {
    onMappingChange(mapping);
    
    // Transform CSV data based on current mapping
    const transformedData: CreateProductInput[] = csvData.map((row) => {
      const product: any = {};
      
      Object.entries(mapping).forEach(([csvHeader, productField]) => {
        if (productField !== 'ignore' && row[csvHeader] !== undefined) {
          const value = row[csvHeader];
          
          // Type conversion for metalPercentage
          if (productField === 'metalPercentage' && value !== '') {
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
              product[productField] = numValue;
            }
          } else if (value !== '') {
            product[productField] = value;
          }
        }
      });
      
      return product;
    }).filter(product => product.sku && product.name); // Filter out invalid rows
    
    onValidDataChange(transformedData);
  }, [mapping, csvData, onMappingChange, onValidDataChange]);

  const handleMappingChange = (csvHeader: string, productField: keyof CreateProductInput | 'ignore') => {
    setMapping(prev => ({
      ...prev,
      [csvHeader]: productField
    }));
  };

  const getMappingStatus = (csvHeader: string) => {
    const mapped = mapping[csvHeader];
    if (!mapped || mapped === 'ignore') return 'unmapped';
    
    const requiredFields = ['sku', 'name', 'htsCode', 'countryOfOrigin'];
    if (requiredFields.includes(mapped)) return 'required';
    return 'optional';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'required': return 'success';
      case 'optional': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'required': return <CheckCircleIcon fontSize="small" />;
      case 'optional': return <img src={InfoIcon} alt="Info" style={{ width: 20, height: 20 }} />;
      default: return <WarningIcon fontSize="small" />;
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Column Mapping
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Map your CSV columns to product fields. Required fields are marked with *.
        </Typography>

        {validationResults && !validationResults.valid && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Missing required columns: {validationResults.missingRequired.join(', ')}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {csvHeaders.map((header) => {
            const status = getMappingStatus(header);
            return (
              <Grid item xs={12} md={6} key={header}>
                <Box 
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Typography variant="body2" fontWeight={500}>
                    {header}
                  </Typography>
                  <Chip
                    size="small"
                    label={status}
                    color={getStatusColor(status) as any}
                    icon={getStatusIcon(status)}
                  />
                </Box>
                <FormControl 
                  fullWidth 
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                >
                  <InputLabel>Map to Field</InputLabel>
                  <Select
                    value={mapping[header] || 'ignore'}
                    label="Map to Field"
                    onChange={(e) => handleMappingChange(header, e.target.value as any)}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                  >
                    {Object.entries(PRODUCT_FIELDS).map(([value, label]) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Data Preview */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Data Preview
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Preview of first 3 rows with current mapping
        </Typography>

        <TableContainer sx={{ mt: 2, maxHeight: 300 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {Object.values(PRODUCT_FIELDS).filter(field => field !== 'Ignore Column').map((field) => (
                  <TableCell key={field} sx={{ fontWeight: 'bold' }}>
                    {field}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {csvData.slice(0, 3).map((row, index) => (
                <TableRow key={index}>
                  {Object.entries(PRODUCT_FIELDS).filter(([key]) => key !== 'ignore').map(([key]) => {
                    // Find the CSV header mapped to this product field
                    const csvHeader = Object.entries(mapping).find(([_, productField]) => productField === key)?.[0];
                    const value = csvHeader ? row[csvHeader] : '';
                    
                    return (
                      <TableCell key={key}>
                        {value || '-'}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Format Guidelines */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Format Guidelines
        </Typography>
        <Stack spacing={1} divider={<Divider />}>
          {Object.entries(FORMAT_GUIDELINES).map(([field, guideline]) => (
            <Box key={field} sx={{ py: 1 }}>
              <Typography variant="body2" fontWeight={500} gutterBottom>
                {PRODUCT_FIELDS[field as keyof typeof PRODUCT_FIELDS] || field}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {guideline}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
};

export default CSVColumnMapper;